import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import {
  BuildingOfficeIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  PlusCircleIcon,
  LightBulbIcon,
  ArrowRightIcon,
  ServerIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

export default function OrgsGuidePage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Orgs Guide</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mr-3" />
            Manage Your Salesforce Organizations
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Learn how to connect Salesforce orgs, manage environments, sync flows, and configure organization settings in FlowDocs.
          </p>
        </div>

        {/* Overview Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Understanding the Orgs Page</h2>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-gray-700 mb-4">
              The Orgs page is your central hub for managing all connected Salesforce organizations. From here, you can connect new orgs, organize them into environments (production and sandboxes), sync flows, and configure settings.
            </p>

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-md">
                <h3 className="font-medium text-blue-900 mb-2">What You Can Do:</h3>
                <ul className="space-y-1 text-sm text-blue-700">
                  <li>• Connect Salesforce production orgs</li>
                  <li>• Connect sandbox environments</li>
                  <li>• Sync flows from Salesforce</li>
                  <li>• Manage environment groupings</li>
                  <li>• Configure org-specific settings</li>
                </ul>
              </div>

              <div className="p-4 bg-purple-50 rounded-md">
                <h3 className="font-medium text-purple-900 mb-2">Organization Types:</h3>
                <ul className="space-y-1 text-sm text-purple-700">
                  <li>• <strong>Production:</strong> Live Salesforce org</li>
                  <li>• <strong>Sandbox:</strong> Testing environment</li>
                  <li>• <strong>Developer Edition:</strong> Free development org</li>
                  <li>• <strong>Scratch Org:</strong> Temporary Salesforce DX org</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Connecting Orgs Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Connecting a Salesforce Org</h2>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <PlusCircleIcon className="h-6 w-6 text-blue-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Organization</h3>
                <p className="mt-1 text-gray-600">
                  Click the "Add Salesforce Org" button to start the OAuth connection flow.
                </p>

                <ol className="mt-4 space-y-3 text-sm text-gray-600">
                  <li>
                    <strong>1. Click "Add Salesforce Org":</strong> Opens Salesforce login
                  </li>
                  <li>
                    <strong>2. Choose Org Type:</strong> Select Production or Sandbox
                  </li>
                  <li>
                    <strong>3. Authorize FlowDocs:</strong> Grant read-only access to flows
                  </li>
                  <li>
                    <strong>4. Initial Sync:</strong> FlowDocs automatically syncs your flows
                  </li>
                </ol>

                <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Permissions Required:</strong> FlowDocs needs "View Setup and Configuration" and "API Enabled" permissions to read flow metadata.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Environments Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Understanding Environments</h2>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <ServerIcon className="h-6 w-6 text-purple-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Environment Grouping</h3>
                <p className="mt-1 text-gray-600">
                  FlowDocs automatically groups your orgs into environments based on their production org relationship. Each environment can contain:
                </p>

                <ul className="mt-3 space-y-2 text-sm text-gray-600">
                  <li>• <strong>1 Production Org:</strong> Your live Salesforce instance</li>
                  <li>• <strong>Up to 4 Sandboxes:</strong> Testing and development environments</li>
                </ul>

                <div className="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <strong>Environment Limits:</strong> Professional plans include 1 environment. Additional environments can be purchased for $99/month each.
                  </p>
                </div>

                <div className="mt-4">
                  <h4 className="font-medium text-gray-900">How Sandboxes Are Identified:</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    FlowDocs automatically detects sandbox org types and groups them with their parent production org based on the org name pattern.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Syncing Flows Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Syncing Flows</h2>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <ArrowPathIcon className="h-6 w-6 text-green-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Manual Flow Sync</h3>
                <p className="mt-1 text-gray-600">
                  Each org card has a "Sync Flows" button to manually pull the latest flow metadata from Salesforce.
                </p>

                <div className="mt-4 space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900">When to Sync:</h4>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600">
                      <li>• After making changes to flows in Salesforce</li>
                      <li>• When you need the most up-to-date analysis</li>
                      <li>• Before exporting flow documentation</li>
                      <li>• After deploying flows between environments</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">What Gets Synced:</h4>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600">
                      <li>• Flow metadata (names, descriptions, types)</li>
                      <li>• Flow versions (active and latest)</li>
                      <li>• Entry criteria and trigger information</li>
                      <li>• Process types and statuses</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-700">
                    <strong>Sync Duration:</strong> Typical sync takes 1-5 minutes depending on the number of flows in your org. Large orgs (100+ flows) may take longer.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Org Settings Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Organization Settings</h2>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <Cog6ToothIcon className="h-6 w-6 text-gray-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Configure Org Settings</h3>
                <p className="mt-1 text-gray-600">
                  Access org-specific settings through the dropdown menu on each org card.
                </p>

                <div className="mt-4 space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900">Available Actions:</h4>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600">
                      <li>• <strong>View Settings:</strong> See org configuration details</li>
                      <li>• <strong>Reconnect:</strong> Refresh OAuth connection if expired</li>
                      <li>• <strong>Disconnect:</strong> Remove org from FlowDocs (data preserved)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Connection Status:</h4>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600">
                      <li>• <strong>Green checkmark:</strong> Connected and active</li>
                      <li>• <strong>Yellow warning:</strong> Connection needs refresh</li>
                      <li>• <strong>Red error:</strong> Connection failed, reconnect required</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Best Practices</h2>

          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-start">
              <LightBulbIcon className="h-6 w-6 text-yellow-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Organization Management Tips</h3>
                <ol className="space-y-3 text-gray-700">
                  <li>
                    <strong>1. Connect Sandboxes Early:</strong>
                    <p className="text-sm mt-1">Connect both production and sandboxes to track flow changes across your deployment pipeline.</p>
                  </li>
                  <li>
                    <strong>2. Regular Syncs:</strong>
                    <p className="text-sm mt-1">Sync flows weekly or after major deployments to keep your documentation up to date.</p>
                  </li>
                  <li>
                    <strong>3. Use Descriptive Names:</strong>
                    <p className="text-sm mt-1">If needed, rename orgs in Salesforce with clear names like "Acme Corp - Production" for easy identification.</p>
                  </li>
                  <li>
                    <strong>4. Monitor Connection Health:</strong>
                    <p className="text-sm mt-1">Check for connection warnings regularly and reconnect if OAuth tokens expire.</p>
                  </li>
                  <li>
                    <strong>5. Plan for Environments:</strong>
                    <p className="text-sm mt-1">If you have multiple business units with separate production orgs, consider purchasing additional environments.</p>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Common Questions */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Common Questions</h2>

          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-2">❓ How many orgs can I connect?</h3>
              <p className="text-sm text-gray-600">
                Professional plans include 1 environment (1 production + up to 4 sandboxes). You can purchase additional environments for $99/month each.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-2">❓ Does FlowDocs modify my Salesforce org?</h3>
              <p className="text-sm text-gray-600">
                No. FlowDocs has read-only access. We only retrieve flow metadata for analysis and documentation. We never modify your flows or data.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-2">❓ What happens if I disconnect an org?</h3>
              <p className="text-sm text-gray-600">
                Disconnecting removes the OAuth connection but preserves your flow data and analysis. You can reconnect at any time to resume syncing.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-2">❓ Can I sync specific flows instead of all flows?</h3>
              <p className="text-sm text-gray-600">
                Currently, syncing pulls all flows from your org. You can use filters on the Flows page to focus on specific flows after syncing.
              </p>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Next Steps</h2>

          <div className="grid gap-4">
            <Link
              to={`/orgs`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Manage Your Orgs</h4>
                    <p className="text-sm text-gray-600">Connect and sync your Salesforce organizations</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>

            <Link
              to={`${basePath}/flows`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ArrowPathIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">View Your Flows</h4>
                    <p className="text-sm text-gray-600">Explore flows from your connected orgs</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 flex items-center">
              <ArrowRightIcon className="h-4 w-4 mr-1 rotate-180" />
              Back to Help Center
            </Link>
            <Link to={`/orgs`} className="text-blue-600 hover:text-blue-800 flex items-center">
              Manage Organizations
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
